
import Vue from 'vue';
import { WhatsNewUpdate, WhatsNewFeature } from '@run-diffusion/shared';
import { v4 as uuidv4 } from 'uuid';
import BaseButton from '@/components/base/BaseButton.vue';
import { ROUTER } from '@/router/constants';
import moment from 'moment-timezone';
import { mapActions } from 'vuex';
import { SNACKBAR_STATUS } from '@/constants/constants';
import DatePicker from '@/components/base/DatePicker.vue';
import { get$bindFirestoreOptions } from '@/mixins';
import { db } from '@/firebase';
import DoubleConfirmDangerDialog from '@/components/base/DoubleConfirmDangerDialog.vue';
import HtmlField from '@/components/HtmlField.vue';
import TimePicker from '@/components/base/TimePicker.vue';
import _omit from 'lodash/omit';
import _cloneDeep from 'lodash/cloneDeep';
import { combineDateAndTime, convertMomentToDateTimeInputValues, getUtcMoment } from '@/utils';

// Define a type for the update form data
interface UpdateFormData {
	id?: string
	version: string
	date: string // Edit state format is "YYYY-MM-DD"
	publishedAtDate: string | null // Edit state format is "YYYY-MM-DD"
	publishedAtTimeInMinutes: number | null
	features: WhatsNewFeature[]
	title?: string
	subTitle?: string
	callToActionText?: string
	callToActionType?: string
	callToActionToolId?: string
}

export default Vue.extend({
	name: 'ManageUpdatesPage',
	metaInfo: {
		title: 'Manage Updates | Admin',
	},
	components: {
		BaseButton,
		DatePicker,
		DoubleConfirmDangerDialog,
		HtmlField,
		TimePicker,
	},
	data () {
		return {
			updates: [] as WhatsNewUpdate[],
			loading: true,
			error: null as Error | null,
			dialog: false,
			editingUpdate: null as UpdateFormData | null,
			saving: false, // Flag for save operation in progress
			deleting: false, // Flag for delete operation in progress
			formValid: true,
			versionRules: [
				v => !!v || 'Version is required',
				v => /^[0-9]+\.[0-9]+\.[0-9]+$/.test(v) || 'Version must be in the format x.x.x'
			],
			dateRules: [
				v => !!v || 'Date is required',
				v => moment(v).isValid() || 'Invalid date format'
			],
			featureTitleRules: [
				v => !!v || 'Feature title is required',
				v => v.length <= 100 || 'Feature title must be less than 100 characters'
			],
			titleRules: [
				v => !!v || 'Title is required',
				v => !v || v.length <= 100 || 'Title must be less than 100 characters'
			],
			subtitleRules: [
				v => !!v || 'Subtitle is required',
				v => !v || v.length <= 200 || 'Subtitle must be less than 200 characters'
			],
			publishConfirmOpen: false,
			publishConfirmUpdate: null as WhatsNewUpdate | null,
			deleteConfirmOpen: false,
			deleteConfirmUpdateId: null as string | null,
			callToActionTypes: [
				{ text: 'Custom', value: 'custom' },
				{ text: 'Go to Tool', value: 'go-to-tool' },
			],
		};
	},
	computed: {
		// sortedUpdates property is removed as we're now sorting at the database level
		isUpdatePublished(): boolean {
			if (!this.editingUpdate || !this.editingUpdate.publishedAtDate) {
				return false;
			}
			const combinedDate: Date = combineDateAndTime(this.editingUpdate.publishedAtDate, this.editingUpdate.publishedAtTimeInMinutes);
			return moment(combinedDate).isBefore(moment());
		},
		getPublishStatusText(): string {
			if (!this.editingUpdate.publishedAtDate) {
				return 'Draft';
			}
			if (this.isUpdatePublished) {
				return 'Published';
			}
			return 'Scheduled';
		}
	},
	async mounted () {
		await this.bindUpdates();
	},
	methods: {
		...mapActions([
			'updateSnackbar',
		]),
		getUtcMoment,
		setPublishedAtDate(dateStr: string) {
			this.editingUpdate = {
				...this.editingUpdate,
				publishedAtDate: dateStr,
			};
		},
		setPublishedAtTimeInMinutes(timeInMinutes: number) {
			this.editingUpdate = {
				...this.editingUpdate,
				publishedAtTimeInMinutes: timeInMinutes,
			};
		},
		isUpdatePublishedHelper(update: WhatsNewUpdate): boolean {
			if (!update.publishedAt) {
				return false;
			}
			return moment(update.publishedAt.toMillis()).isBefore(moment());
		},
		getUpdateStatusText(update: WhatsNewUpdate): string {
			if (!update.publishedAt) {
				return 'Draft';
			}
			if (this.isUpdatePublishedHelper(update)) {
				return 'Published';
			}
			return 'Scheduled';
		},

		async bindUpdates () {
			this.loading = true;
			this.error = null;
			try {
				// Get all updates (both published and unpublished) for admin management
				// index created: whatsNewUpdates: isPublished Ascending date Descending __name__ Descending
				const updatesRef = db
					.collection('whatsNewUpdates')
					// Sort by date in descending order (newest first)
					.orderBy('date', 'desc');

				await this.$bind(
					'updates',
					updatesRef,
					get$bindFirestoreOptions(),
				);
			} catch (err: any) {
				console.error('Error binding updates:', err);
				this.error = err;
			} finally {
				this.loading = false;
			}
		},

		openAddDialog () {
			this.editingUpdate = {
				version: '',
				date: moment().format('YYYY-MM-DD'), // Default to today
				publishedAtDate: null,
				publishedAtTimeInMinutes: null,
				features: [{
					title: '',
					description: '',
					bulletPoints: [],
					icon: ''
				}],
				title: '',
				subTitle: '',
				callToActionText: '',
				callToActionType: 'custom', // Default to custom
				callToActionToolId: '' // Empty by default
			};
			this.dialog = true;
		},

		openEditDialog (update: WhatsNewUpdate) {
			// Create a deep copy of the features and ensure each feature has the required properties
			const features = _cloneDeep(update.features).map(feature => {
				// Ensure bulletPoints exist (required in firebase.ts but might be missing in UI)
				if (!feature.bulletPoints) {
					feature.bulletPoints = [];
				}
				return feature;
			});

			const { dateStr, timeInMinutes } = convertMomentToDateTimeInputValues(update.publishedAt ? moment(update.publishedAt.toMillis()) : null);
			this.editingUpdate = {
				..._omit(update, ['isPublished', 'publishedAt']),
				id: update.id, // Explicitly set ID to ensure it's preserved
				date: update.date ? getUtcMoment(update.date.toMillis()).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD'),
				features: features,
				callToActionText: update.callToActionText || '',
				callToActionType: update.callToActionType || 'custom',
				callToActionToolId: update.callToActionToolId || '',
				publishedAtDate: dateStr,
				publishedAtTimeInMinutes: timeInMinutes,
			};
			this.dialog = true;
		},

		closeDialog () {
			this.dialog = false;
			this.editingUpdate = null;
			this.saving = false; // Reset saving flag
		},

		addFeature () {
			if (this.editingUpdate) {
				this.editingUpdate.features.push({
					title: '',
					description: '',
					bulletPoints: [],
					icon: ''
				});
			}
		},

		removeFeature (index: number) {
			if (this.editingUpdate && this.editingUpdate.features.length > 1) {
				this.editingUpdate.features.splice(index, 1);
			}
		},

		async saveUpdate () {
			if (!this.editingUpdate) return;

			// Validate the form
			if (this.$refs.updateForm && !(this.$refs.updateForm as Vue & { validate: () => boolean }).validate()) {
				// Form validation failed, show toast
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Please correct the validation errors before saving',
					show: true,
				});
				return;
			}

			this.saving = true; // Set saving flag

			// Filter out empty features
			const features = this.editingUpdate.features.filter(
				(f) => f.title && f.description
			);

			try {
				const id: string = this.editingUpdate.id || uuidv4();
				const docRef = db.collection('whatsNewUpdates').doc(id);

				const publishedAt: Date = combineDateAndTime(this.editingUpdate.publishedAtDate, this.editingUpdate.publishedAtTimeInMinutes);
				if (this.editingUpdate.id) {
					// Update existing document
					await docRef.update({
						version: this.editingUpdate.version,
						date: getUtcMoment(this.editingUpdate.date).toDate(),
						publishedAt,
						isPublished: !!publishedAt,
						features: features,
						title: this.editingUpdate.title || null,
						subTitle: this.editingUpdate.subTitle || null,
						callToActionText: this.editingUpdate.callToActionText || null,
						callToActionType: this.editingUpdate.callToActionType || null,
						callToActionToolId: this.editingUpdate.callToActionToolId || null,
					});
				} else {
					// Create new document
					await docRef.set({
						createdAt: new Date(),
						version: this.editingUpdate.version,
						date: getUtcMoment(this.editingUpdate.date).toDate(),
						publishedAt,
						isPublished: !!publishedAt,
						features: features,
						title: this.editingUpdate.title || null,
						subTitle: this.editingUpdate.subTitle || null,
						callToActionText: this.editingUpdate.callToActionText || null,
						callToActionType: this.editingUpdate.callToActionType || null,
						callToActionToolId: this.editingUpdate.callToActionToolId || null,
					});
				}

				// Show success toast
				this.updateSnackbar({
					status: SNACKBAR_STATUS.SUCCESS,
					message: this.editingUpdate.id ? 'Update successfully modified' : 'New update successfully created',
					show: true,
				});

				this.closeDialog();
				// No need to rebind - changes will be reflected automatically
			} catch (err: any) {
				console.error('Error saving update:', err);
				this.error = err;
				this.saving = false; // Reset saving flag on error

				// Show error toast
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: `Error saving update: ${err.message}`,
					show: true,
				});
			}
			// Saving flag is reset in closeDialog on success
		},

		openPublishConfirmDialog (update: WhatsNewUpdate) {
			this.publishConfirmUpdate = update;
			this.publishConfirmOpen = true;
		},
		closePublishConfirmDialog () {
			this.publishConfirmOpen = false;
			this.publishConfirmUpdate = null;
		},
		async confirmTogglePublish () {
			if (!this.publishConfirmUpdate) return;
			const update: WhatsNewUpdate = this.publishConfirmUpdate; // Capture the update before closing dialog
			const isCurrentlyPublished: boolean = this.isUpdatePublishedHelper(update);
			this.closePublishConfirmDialog(); // Close dialog immediately

			const docRef = db.collection('whatsNewUpdates').doc(update.id);
			try {
				if (isCurrentlyPublished) {
					// Unpublish - set publishedAt to null
					await docRef.update({
						publishedAt: null,
						isPublished: false,
					});
				} else {
					// Publish - set publishedAt to now
					await docRef.update({
						publishedAt: new Date(),
						isPublished: true,
					});
				}
				this.updateSnackbar({
					status: SNACKBAR_STATUS.SUCCESS,
					message: `Update ${isCurrentlyPublished ? 'unpublished' : 'published'} successfully`,
					show: true,
				});
			} catch (err: any) {
				console.error('Error toggling publish status:', err);
				this.error = err;
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: `Error toggling publish status: ${err.message}`,
					show: true,
				});
			}
		},

		openDeleteConfirmDialog (id: string) {
			this.deleteConfirmUpdateId = id;
			this.deleteConfirmOpen = true;
		},
		closeDeleteConfirmDialog () {
			this.deleteConfirmOpen = false;
			this.deleteConfirmUpdateId = null;
			this.deleting = false; // Also reset deleting flag on close/cancel
		},
		async confirmDeleteUpdate () {
			if (!this.deleteConfirmUpdateId) return;
			const id = this.deleteConfirmUpdateId; // Capture id before closing

			this.deleting = true; // Set deleting flag
			this.closeDeleteConfirmDialog(); // Close dialog

			const docRef = db.collection('whatsNewUpdates').doc(id);
			try {
				await docRef.delete();
				this.updateSnackbar({
					status: SNACKBAR_STATUS.SUCCESS,
					message: 'Update deleted successfully',
					show: true,
				});
			} catch (err: any) {
				console.error('Error deleting update:', err);
				this.error = err;
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: `Error deleting update: ${err.message}`,
					show: true,
				});
			} finally {
				this.deleting = false; // Reset deleting flag
			}
		},

		copyUpdateId () {
			if (this.editingUpdate && this.editingUpdate.id) {
				navigator.clipboard.writeText(this.editingUpdate.id)
					.then(() => {
						// Show global snackbar notification
						this.updateSnackbar({
							status: SNACKBAR_STATUS.SUCCESS,
							message: 'Update ID copied to clipboard!',
							show: true,
						});
					})
					.catch(err => {
						console.error('Error copying to clipboard:', err);
						this.updateSnackbar({
							status: SNACKBAR_STATUS.ERROR,
							message: 'Failed to copy update ID',
							show: true,
						});
					});
			}
		},

		goBack () {
			this.routerPush(this.$route, this.$router, { name: ROUTER.RUNNITS_WHATS_NEW });
		},

		openPreviewDialog (update: WhatsNewUpdate) {
			// Instead of opening a dialog, navigate to WhatsNewPage with preview data
			this.routerPush(this.$route, this.$router, {
				name: ROUTER.RUNNITS_WHATS_NEW,
				query: { preview: 'true' },
				// Pass the update data via route params
				params: { previewData: JSON.stringify(update) }
			});
		},

		formatCTAType (type: string | undefined): string {
			if (!type) return 'Custom';

			switch (type) {
				case 'go-to-tool':
					return 'Go to Tool';
				case 'custom':
					return 'Custom';
				default:
					return type.charAt(0).toUpperCase() + type.slice(1).replace(/-/g, ' ');
			}
		},
	}
});
